/**
 * Facet filter toolbar — Deakin filter-toolbar with dropdowns and Reset.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  SearchAppContext,
  SearchAppDispatchContext,
} from '../../context/search-app/search-app-context';
import {
  CLEAR_FILTERS,
  SET_ALL_FACETS,
  SET_FACET,
} from '../../context/search-app/search-app-actions';
import { hasActiveFacets } from '../../context/search-app/has-active-facets';
import { normalizeSelectedFacetsForRequestKey } from '../../context/search-app/funnelback-toggle-url';
import { facetIdsEqual } from '../../context/search-app/facet-id';
import { DEAKIN_TAGS_FACET_ID } from '../../funnelback/deakin-config';
import FacetFilterDropdown, {
  type FunnelbackFacetFilterFacet,
  facetHasSelectableOptions,
  resolveFacetId,
} from '../facet-filter';

const BASE_CLASS = 'filter-toolbar';

function cloneFacetMap(
  source: Record<string, string[]>,
): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(source)) {
    next[key] = [...values];
  }
  return next;
}

function facetSelectionKey(facets: Record<string, string[]>): string {
  return JSON.stringify(normalizeSelectedFacetsForRequestKey(facets));
}

const FacetFilters = () => {
  const state = useContext(SearchAppContext);
  const { dispatch } = useContext(SearchAppDispatchContext);
  const facets = (state.funnelbackFacets ?? []) as FunnelbackFacetFilterFacet[];

  const facetsForDisplay = useMemo(
    () =>
      facets.filter((facet) => {
        const facetId = resolveFacetId(facet);
        return (
          facetId !== null &&
          !facetIdsEqual(facetId, DEAKIN_TAGS_FACET_ID) &&
          facetHasSelectableOptions(facet)
        );
      }),
    [facets],
  );

  const [draftFacets, setDraftFacets] = useState<Record<string, string[]>>(() =>
    cloneFacetMap(state.selectedFacets),
  );
  const [openFacetName, setOpenFacetName] = useState<string | null>(null);

  useEffect(() => {
    setDraftFacets(cloneFacetMap(state.selectedFacets));
  }, [state.selectedFacets]);

  const updateDraftFacet = useCallback((facetId: string, values: string[]) => {
    setDraftFacets((prev) => {
      const next = { ...prev };
      if (values.length === 0) {
        delete next[facetId];
      } else {
        next[facetId] = values;
      }
      return next;
    });
  }, []);

  const applyFacet = useCallback(
    (facetId: string) => {
      dispatch({
        type: SET_FACET,
        payload: { facetId, values: draftFacets[facetId] ?? [] },
      });
    },
    [dispatch, draftFacets],
  );

  const hasPendingFacetChanges = useMemo(
    () => facetSelectionKey(draftFacets) !== facetSelectionKey(state.selectedFacets),
    [draftFacets, state.selectedFacets],
  );
  const canResetFilters = hasActiveFacets(state.selectedFacets);

  const applyAllFilters = useCallback(() => {
    dispatch({ type: SET_ALL_FACETS, payload: draftFacets });
    setOpenFacetName(null);
  }, [dispatch, draftFacets]);

  const resetAllFilters = useCallback(() => {
    dispatch({ type: CLEAR_FILTERS });
    setDraftFacets({});
    setOpenFacetName(null);
  }, [dispatch]);

  if (facetsForDisplay.length === 0) {
    return null;
  }

  return (
    <>
      {facetsForDisplay.map((facet) => {
        const facetId = resolveFacetId(facet);
        if (!facetId) {
          return null;
        }
        const facetName = facet.name;
        return (
          <FacetFilterDropdown
            key={facetName}
            facet={facet}
            draftValues={draftFacets[facetId] ?? []}
            isOpen={openFacetName === facetName}
            onOpenChange={(open) =>
              setOpenFacetName(open ? facetName : null)
            }
            onDraftChange={(values) => updateDraftFacet(facetId, values)}
            onApply={() => applyFacet(facetId)}
          />
        );
      })}
      <div className="filter-toolbar-buttons">
        <button
          type="button"
          name="filter-toolbar-button--apply"
          className="btn btn--charcoal btn--medium"
          onClick={applyAllFilters}
          disabled={!hasPendingFacetChanges}
          aria-disabled={!hasPendingFacetChanges}
        >
          Apply filters
        </button>
        <button
          type="button"
          name="filter-toolbar-button--reset"
          className="btn btn--charcoal btn--secondary btn--medium"
          onClick={resetAllFilters}
          disabled={!canResetFilters}
          aria-disabled={!canResetFilters}
        >
          Reset
        </button>
      </div>
    </>
  );
};

export default FacetFilters;

export { BASE_CLASS as FILTER_TOOLBAR_CLASS };
